import React, { useEffect, useState, Suspense, lazy } from "react";

import { Layout } from "./layout";
import { PageLoading } from "@/components/page-loading";
import { useLocalStore } from "@/hooks/use-local-store";
import { useShallow } from "zustand/shallow";
import { PageName } from "@/lib/types";
import { apiService } from "@/services/api";
import { MailboxItemProvider } from "@/providers/mailbox-item-provider";
import { Toaster } from "@/components/ui/sonner";
import { ErrorBoundary } from "@/components/error-boundary";

// Lazy load the page components
const ConnectionPage = lazy(() => import("@/pages/connection").then((module) => ({ default: module.ConnectionPage })));
const FilesPage = lazy(() => import("@/pages/files").then((module) => ({ default: module.FilesPage })));
const ErrorPage = lazy(() => import("@/pages/error").then((module) => ({ default: module.ErrorPage })));
const SettingsPage = lazy(() => import("@/pages/settings").then((module) => ({ default: module.SettingsPage })));

function AppRouter({ page }: { page: PageName }) {
  switch (page) {
    case "connection":
      return <ConnectionPage />;
    case "files":
      return <FilesPage />;
    case "settings":
      return <SettingsPage />;
    case "error":
      return <ErrorPage />;
    default:
      return null;
  }
}

export default function App() {
  const { page, setPage, userProfile, setUserProfile, setAutoLogin } = useLocalStore(
    useShallow(({ page, setPage, userProfile, setUserProfile, setAutoLogin }) => ({
      page,
      setPage,
      userProfile,
      setUserProfile,
      setAutoLogin,
    })),
  );
  const [loginChecked, setLoginChecked] = useState(false);

  useEffect(() => {
    if (loginChecked) return;

    // If we already have a stored user profile, perform a quick preflight check
    if (userProfile) {
      // Safety net: if preflight stalls (e.g. proxy hang in a shared-mailbox
      // iframe), unblock the UI so the user sees the cached files view rather
      // than the loading spinner forever. The apiClient's own 30s timeout is
      // too long here.
      const preflightTimeoutId = setTimeout(() => {
        setLoginChecked(true);
      }, 10000);

      const performPreflightCheck = async () => {
        try {
          const profile = await apiService.fetchUserProfile();
          clearTimeout(preflightTimeoutId);
          setUserProfile(profile);
          setLoginChecked(true);
        } catch (error) {
          clearTimeout(preflightTimeoutId);
          console.warn("Preflight check failed, tokens may be invalid:", error);
          setUserProfile(null);
          setAutoLogin(false);
          setPage("connection");
          setLoginChecked(true);
        }
      };

      performPreflightCheck();
      return;
    }

    // Create a timeout to prevent hanging
    const timeoutId = setTimeout(() => {
      setPage("connection");
      setLoginChecked(true);
    }, 5000); // 5 second timeout

    // Check if user is authenticated on app start (only once)
    apiService
      .fetchUserProfile()
      .then((profile) => {
        clearTimeout(timeoutId);
        if (profile) {
          setUserProfile(profile);
          setPage("files");
        } else {
          setUserProfile(null);
          setPage("connection");
        }
      })
      .catch(() => {
        clearTimeout(timeoutId);
        setUserProfile(null);
        setPage("connection");
      })
      .finally(() => {
        setLoginChecked(true);
      });
  }, [loginChecked, setPage, userProfile, setUserProfile, setAutoLogin]);

  if (!loginChecked) {
    return (
      <Layout>
        <PageLoading />
      </Layout>
    );
  }

  return (
    <ErrorBoundary>
      <MailboxItemProvider>
        <Layout>
          <Suspense fallback={<PageLoading />}>
            <AppRouter page={page} />
          </Suspense>
          <Toaster position="top-right" />
        </Layout>
      </MailboxItemProvider>
    </ErrorBoundary>
  );
}
